/* Chrome 3D logo for the hero (sections/image-banner.liquid).
   Turns the logo SVG (assets/sample-logo-3d.svg) into a solid chrome object
   that turns slightly toward the mouse. three.js loads from jsDelivr; if
   anything fails, the section shows the flat logo instead.
   Quality: the SVG is a high-precision outline (0.01 units), the surfaces are
   smooth-shaded, and the canvas is drawn above screen resolution.
   Spin: visitors can grab the logo (mouse or finger) and spin it on all three
   axes; when they let go it keeps spinning, slows down and settles back. */
import {
  WebGLRenderer, Scene, PerspectiveCamera, Group, Mesh, Color, Box3, Vector3, Quaternion, Euler,
  ExtrudeGeometry, MeshPhysicalMaterial, MeshBasicMaterial, PlaneGeometry, BoxGeometry, BufferAttribute,
  PMREMGenerator, ACESFilmicToneMapping, SRGBColorSpace, BackSide, DoubleSide, DirectionalLight, MathUtils
} from 'https://cdn.jsdelivr.net/npm/three@0.169.0/+esm';
import { SVGLoader } from 'https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/loaders/SVGLoader.js/+esm';

const FINISH = {
  mirror: { roughness: 0.07, color: '#f2f2f2' },
  satin: { roughness: 0.22, color: '#e6e6e6' },
  brushed: { roughness: 0.34, color: '#d9d9d9' }
};

// Dark studio with bright softbox strips: this is what the chrome reflects.
function studioEnvironment(renderer) {
  const env = new Scene();
  env.add(new Mesh(new BoxGeometry(40, 24, 40), new MeshBasicMaterial({ color: 0x000000, side: BackSide })));
  const light = (w, h, intensity, x, y, z, rx = 0, ry = 0) => {
    const m = new Mesh(new PlaneGeometry(w, h), new MeshBasicMaterial({ color: new Color().setScalar(intensity), side: DoubleSide }));
    m.position.set(x, y, z); m.rotation.set(rx, ry, 0); env.add(m);
  };
  light(16, 3.2, 6, 0, 11.5, -2, Math.PI / 2, 0);      // top softbox
  light(16, 9, 1.7, -19.8, 1.5, 4, 0, Math.PI / 2);    // soft panel left
  light(16, 9, 1.7, 19.8, 1.5, 4, 0, -Math.PI / 2);    // soft panel right
  light(18, 7, 1.2, 0, 2, -19.8, 0, 0);                // soft panel far wall
  light(0.9, 16, 9, -12, 1, 6, 0, Math.PI / 2);        // left strip
  light(0.9, 16, 9, 12, 1, 6, 0, -Math.PI / 2);        // right strip
  light(30, 7, 2.4, 0, 6.5, 19.8, 0, Math.PI);         // behind viewer: bright top
  light(36, 1.1, 7, 0, -1.2, 19.8, 0, Math.PI);        // behind viewer: horizon streak
  light(30, 5, 0.7, 0, -8, 19.8, 0, Math.PI);          // behind viewer: soft bottom
  light(5, 5, 1.6, -8, 6, -18, 0, 0);                  // back fill
  light(3, 9, 2.2, 9, 4, -16, 0, 0);                   // back fill
  const pmrem = new PMREMGenerator(renderer);
  const tex = pmrem.fromScene(env, 0.035).texture;
  pmrem.dispose();
  env.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) o.material.dispose(); });
  return tex;
}

// Smooth shading. ExtrudeGeometry shades every flat strip of the outline
// separately, which shows up as stripes on the curved walls. This blends the
// normals of neighbouring faces so curves and the rounded bevel look smooth,
// but keeps real corners (more than creaseDeg apart) sharp.
function smoothNormals(geo, creaseDeg = 40) {
  const pos = geo.attributes.position.array;
  const nv = pos.length / 3, nt = nv / 3;
  const area = new Float32Array(nt * 3), unit = new Float32Array(nt * 3);
  for (let t = 0; t < nt; t++) {
    const a = t * 9;
    const ux = pos[a + 3] - pos[a], uy = pos[a + 4] - pos[a + 1], uz = pos[a + 5] - pos[a + 2];
    const vx = pos[a + 6] - pos[a], vy = pos[a + 7] - pos[a + 1], vz = pos[a + 8] - pos[a + 2];
    const x = uy * vz - uz * vy, y = uz * vx - ux * vz, z = ux * vy - uy * vx;
    const l = Math.hypot(x, y, z) || 1;
    area[t * 3] = x; area[t * 3 + 1] = y; area[t * 3 + 2] = z;
    unit[t * 3] = x / l; unit[t * 3 + 1] = y / l; unit[t * 3 + 2] = z / l;
  }
  // group corners that sit at the same point
  const ids = new Int32Array(nv), keyToId = new Map();
  geo.computeBoundingBox();
  const bb = geo.boundingBox, M = 131072;
  const Q = 120000 / Math.max(1e-6, bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
  for (let i = 0; i < nv; i++) {
    const k = (Math.round((pos[i * 3] - bb.min.x) * Q) * M + Math.round((pos[i * 3 + 1] - bb.min.y) * Q)) * M + Math.round((pos[i * 3 + 2] - bb.min.z) * Q);
    let id = keyToId.get(k);
    if (id === undefined) { id = keyToId.size; keyToId.set(k, id); }
    ids[i] = id;
  }
  const count = new Int32Array(keyToId.size + 1);
  for (let i = 0; i < nv; i++) count[ids[i] + 1]++;
  for (let i = 1; i < count.length; i++) count[i] += count[i - 1];
  const fill = count.slice(), tris = new Int32Array(nv);
  for (let i = 0; i < nv; i++) tris[fill[ids[i]]++] = (i / 3) | 0;
  const cos = Math.cos(MathUtils.degToRad(creaseDeg));
  const out = new Float32Array(nv * 3);
  for (let i = 0; i < nv; i++) {
    const t = (i / 3) | 0, id = ids[i];
    const fx = unit[t * 3], fy = unit[t * 3 + 1], fz = unit[t * 3 + 2];
    // the flat front and back stay perfectly flat (their big triangles would
    // show streaks otherwise); the bevel next to them still blends into them
    if (Math.abs(fz) > 0.9999) { out[i * 3] = fx; out[i * 3 + 1] = fy; out[i * 3 + 2] = fz; continue; }
    let x = 0, y = 0, z = 0;
    for (let j = count[id]; j < count[id + 1]; j++) {
      const u = tris[j];
      if (fx * unit[u * 3] + fy * unit[u * 3 + 1] + fz * unit[u * 3 + 2] >= cos) {
        x += area[u * 3]; y += area[u * 3 + 1]; z += area[u * 3 + 2];
      }
    }
    const l = Math.hypot(x, y, z) || 1;
    out[i * 3] = x / l; out[i * 3 + 1] = y / l; out[i * 3 + 2] = z / l;
  }
  geo.setAttribute('normal', new BufferAttribute(out, 3));
  return geo;
}

function buildLogo(svgText, material) {
  const data = new SVGLoader().parse(svgText);
  const group = new Group();
  const probe = new Box3();
  for (const path of data.paths) for (const shape of SVGLoader.createShapes(path)) {
    shape.getPoints(4).forEach((p) => probe.expandByPoint(new Vector3(p.x, p.y, 0)));
  }
  const unit = Math.max(1, probe.max.x - probe.min.x) / 570; // keep proportions for any logo size
  for (const path of data.paths) {
    for (const shape of SVGLoader.createShapes(path)) {
      group.add(new Mesh(smoothNormals(new ExtrudeGeometry(shape, {
        depth: 42.75 * unit, curveSegments: 28, bevelEnabled: true,
        bevelThickness: 3.2 * unit, bevelSize: 2.1 * unit, bevelOffset: -2.1 * unit, bevelSegments: 7
      })), material));
    }
  }
  group.scale.set(1, -1, 1); // SVG y points down
  const box = new Box3().setFromObject(group);
  const c = box.getCenter(new Vector3());
  group.position.set(-c.x, -c.y, -c.z);
  const pivot = new Group();
  pivot.add(group);
  return { pivot, width: box.max.x - box.min.x, height: box.max.y - box.min.y };
}

export function mount(canvas, opts = {}) {
  const o = Object.assign({
    svg: '', finish: 'mirror', background: '#000000',
    widthShare: 0.35, mobileWidthShare: 0.7,
    tiltDeg: 18,          // how far the logo turns toward the mouse (left/right)
    observeEl: null,      // element whose visibility decides when to animate (default: the canvas)
    pixelRatio: 0,        // 0 = automatic (sharper than the screen when the device can handle it)
    spin: true,           // visitors can grab and spin the logo
    onReady: null
  }, opts);
  if (!o.svg) throw new Error('chrome-logo-3d: no SVG');
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const touchOnly = typeof matchMedia === 'function' && matchMedia('(hover: none)').matches;

  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
  renderer.setClearColor(o.background, 1);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new Scene();
  scene.background = new Color(o.background);
  scene.environment = studioEnvironment(renderer);

  const f = FINISH[o.finish] || FINISH.mirror;
  const material = new MeshPhysicalMaterial({
    color: f.color, metalness: 1, roughness: f.roughness, envMapIntensity: 1.25,
    clearcoat: 0.25, clearcoatRoughness: 0.1   // slightly softer top coat: less sparkle on thin edges
  });
  const key = new DirectionalLight(0xffffff, 1.4); key.position.set(-400, 500, 600); scene.add(key);
  const rim = new DirectionalLight(0xffffff, 0.9); rim.position.set(500, -200, -300); scene.add(rim);

  const camera = new PerspectiveCamera(26, 1, 10, 20000);
  const { pivot, width, height } = buildLogo(o.svg, material);
  scene.add(pivot);

  // Mouse tracking: the logo turns a little toward the cursor and eases back
  // to the centre when the mouse leaves the window.
  const maxY = MathUtils.degToRad(o.tiltDeg);
  const maxX = MathUtils.degToRad(o.tiltDeg * 0.6);
  const target = { x: 0, y: 0 }, cur = { x: 0, y: 0 };
  const trackQ = new Quaternion(), trackE = new Euler(0, 0, 0, 'YXZ');

  // Grab and spin. `spinQ` is the extra turn from dragging/spinning on top of
  // the mouse tracking (no turn = the normal pose).
  const spinOn = o.spin !== false;
  const spinQ = new Quaternion(), restQ = new Quaternion(), stepQ = new Quaternion();
  const omega = new Vector3();          // spin speed and direction (radians per second)
  const step = new Vector3(), unitAxis = new Vector3();
  const MAX_SPIN = 14;                  // top speed after a flick: about 2 turns per second
  const FRICTION = reduce ? 4 : 0.8;    // how quickly a spin slows down (reduced motion: fast)
  let mode = 0;                         // 0 = normal, 1 = being dragged, 2 = spinning after release
  let last = null;                      // previous drag point
  // When the input actually happened (the page may handle it a little later)
  const evTime = (e) => {
    const now = performance.now(), t = e && e.timeStamp;
    return t > 0 && t <= now + 50 && now - t < 1000 ? t : now;
  };

  // Where the logo sits on screen (its normal pose), for "is the press on the logo?"
  function logoRect() {
    const r = canvas.getBoundingClientRect();
    const share = canvas.clientWidth < 750 ? o.mobileWidthShare : o.widthShare;
    const hw = (r.width * share) / 2;
    return { cx: r.left + r.width / 2, cy: r.top + r.height / 2, hw, hh: hw * (height / width) };
  }
  function overLogo(x, y) {
    const L = logoRect(), pad = 12;
    return Math.abs(x - L.cx) <= L.hw + pad && Math.abs(y - L.cy) <= L.hh + pad;
  }

  function dragStart(x, y, t) {
    mode = 1;
    omega.set(0, 0, 0);
    last = { x, y, t };
    canvas.style.cursor = 'grabbing';
  }
  // Dragging across the logo tips it over (left/right, up/down, any diagonal);
  // circling around its edge twists it like a steering wheel (the third axis).
  function dragMove(x, y, now) {
    if (mode !== 1) return;
    const L = logoRect();
    const dx = x - last.x, dy = -(y - last.y);            // screen y points down, 3D y points up
    const px = last.x - L.cx, py = -(last.y - L.cy);      // previous point, from the logo centre
    const dts = Math.max(0.004, (now - last.t) / 1000);
    last = { x, y, t: now };
    if (!dx && !dy) return;
    const R = Math.max(L.hw, 60);                         // dragging one logo-width ≈ 115°
    const pr = Math.hypot(px, py) || 1e-6;
    const ux = px / pr, uy = py / pr;
    const radial = dx * ux + dy * uy;                     // towards / away from the centre
    const around = -dx * uy + dy * ux;                    // around the centre (anticlockwise +)
    const w = MathUtils.smoothstep(pr, R * 0.6, R * 1.4); // 0 in the middle, 1 at the edge
    const tx = radial * ux - (1 - w) * around * uy;       // part of the drag that tips the logo
    const ty = radial * uy + (1 - w) * around * ux;
    const twist = (w * around) / Math.max(pr, R * 0.6);  // part that twists it
    step.set(-ty / R, tx / R, twist);                     // this move's turn as axis × angle
    const a = step.length();
    if (a > 0) spinQ.premultiply(stepQ.setFromAxisAngle(unitAxis.copy(step).divideScalar(a), a));
    // speed for the release: averaged over the last few moves
    omega.lerp(step.divideScalar(dts), 1 - Math.exp(-dts / 0.05));
  }
  function dragEnd(t) {
    if (mode !== 1) return;
    // held still before letting go: little or no spin
    const still = Math.max(0, (t - last.t) / 1000);
    omega.multiplyScalar(Math.exp(-still / 0.06));
    if (omega.length() > MAX_SPIN) omega.setLength(MAX_SPIN);
    mode = 2;
    canvas.style.cursor = '';
  }

  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    if (mode === 1) { dragMove(e.clientX, e.clientY, evTime(e)); return; }   // tracking pauses while dragging
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const nx = MathUtils.clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1);
    const ny = MathUtils.clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1);
    target.y = nx * maxY;
    target.x = ny * maxX;
    if (spinOn) canvas.style.cursor = overLogo(e.clientX, e.clientY) ? 'grab' : '';
  };
  const onLeave = () => { target.x = 0; target.y = 0; };
  const onBlur = () => { dragEnd(performance.now()); onLeave(); };
  window.addEventListener('pointermove', onMove, { passive: true });
  document.documentElement.addEventListener('mouseleave', onLeave);
  window.addEventListener('blur', onBlur);

  // Mouse / pen
  const onDown = (e) => {
    if (e.pointerType === 'touch' || e.button !== 0 || !overLogo(e.clientX, e.clientY)) return;
    e.preventDefault();                                   // no text selection while dragging
    try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* not needed to work */ }
    dragStart(e.clientX, e.clientY, evTime(e));
  };
  const onUp = (e) => { if (e.pointerType !== 'touch') dragEnd(evTime(e)); };
  // Fingers: a touch that starts on the logo spins it; anywhere else the page scrolls as usual.
  let touchId = null;
  const findTouch = (list) => {
    for (let i = 0; i < list.length; i++) if (list[i].identifier === touchId) return list[i];
    return null;
  };
  const onTouchStart = (e) => {
    if (touchId !== null || e.touches.length !== 1) return;
    const t = e.changedTouches[0];
    if (!overLogo(t.clientX, t.clientY)) return;
    e.preventDefault();
    touchId = t.identifier;
    dragStart(t.clientX, t.clientY, evTime(e));
  };
  const onTouchMove = (e) => {
    if (touchId === null) return;
    const t = findTouch(e.changedTouches);
    if (!t) return;
    e.preventDefault();
    dragMove(t.clientX, t.clientY, evTime(e));
  };
  const onTouchEnd = (e) => {
    if (touchId === null || !findTouch(e.changedTouches)) return;
    touchId = null;
    dragEnd(evTime(e));
  };
  if (spinOn) {
    canvas.style.userSelect = canvas.style.webkitUserSelect = 'none';
    canvas.style.webkitTouchCallout = 'none';
    canvas.style.webkitTapHighlightColor = 'transparent';
    canvas.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
  }

  // Sharpness: draw at 1.5x the screen's resolution (up to 2.5x) and let the
  // browser scale it down, which smooths the thin chrome edges. If the device
  // can't keep up (checked over the first second), it drops to normal resolution.
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  let pixelRatio = o.pixelRatio || Math.min(dpr * 1.5, 2.5);
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const share = w < 750 ? o.mobileWidthShare : o.widthShare;
    const viewH = (width / share) / camera.aspect;
    camera.position.set(0, 0, viewH / (2 * Math.tan(MathUtils.degToRad(camera.fov / 2))));
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  if (ro) ro.observe(canvas); else window.addEventListener('resize', resize);
  resize();

  // Only animate while the hero is on screen. The canvas is pinned to the screen
  // on desktop (parallax), so watch the banner itself, not the canvas.
  let raf = 0, visible = true, readyCalled = false, prevNow = performance.now();
  let perfFrames = 0, prevFrame = 0; const frameTimes = [];
  const t0 = performance.now();
  const io = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(([en]) => { visible = en.isIntersecting; if (visible && !raf) { prevNow = performance.now(); raf = requestAnimationFrame(frame); } })
    : null;
  if (io) io.observe(o.observeEl || canvas);

  function frame(now) {
    raf = 0;
    const dt = Math.min(0.05, Math.max(0, (now - prevNow) / 1000)); prevNow = now;
    // Phones and tablets have no mouse: a slow, gentle sway keeps the chrome alive.
    if (touchOnly && !reduce) {
      const t = (now - t0) / 1000;
      target.y = Math.sin(t * 0.6) * maxY * 0.6;
      target.x = Math.sin(t * 0.45) * maxX * 0.35;
    }
    const k = 1 - Math.exp(-dt * 5);   // smooth, frame-rate independent easing
    cur.x += (target.x - cur.x) * k;
    cur.y += (target.y - cur.y) * k;
    // After a flick: keep spinning, slow down, and once it's slow, settle back
    // (the shortest way) into the normal mouse-tracking pose.
    if (mode === 2) {
      const speed = omega.length();
      const back = 1 - MathUtils.smoothstep(speed, 1, 4);   // 0 while fast, 1 once slow
      if (speed > 1e-4) spinQ.premultiply(stepQ.setFromAxisAngle(unitAxis.copy(omega).divideScalar(speed), speed * dt));
      omega.multiplyScalar(Math.exp(-dt * (FRICTION + 3 * back)));
      spinQ.slerp(restQ, 1 - Math.exp(-dt * (reduce ? 8 : 4) * back));
      if (speed < 0.01 && 1 - Math.abs(spinQ.w) < 1e-7) { spinQ.identity(); omega.set(0, 0, 0); mode = 0; }
    }
    trackQ.setFromEuler(trackE.set(cur.x, cur.y, 0, 'YXZ'));
    pivot.quaternion.copy(spinQ).multiply(trackQ);
    renderer.render(scene, camera);
    if (!readyCalled) { readyCalled = true; if (o.onReady) o.onReady(); }
    else if (!o.pixelRatio && pixelRatio > Math.min(dpr, 2) && perfFrames < 60) {
      if (now - prevFrame < 100) frameTimes.push(now - prevFrame); // ignore pauses (tab switches)
      if (++perfFrames === 60) {
        frameTimes.sort((a, b) => a - b);
        if (!frameTimes.length || frameTimes[frameTimes.length >> 1] > 24) { pixelRatio = Math.min(dpr, 2); resize(); }
      }
    }
    prevFrame = now;
    if (visible) raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    dispose() {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('blur', onBlur);
      canvas.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      if (ro) ro.disconnect(); else window.removeEventListener('resize', resize);
      if (io) io.disconnect();
      pivot.traverse((m) => m.geometry && m.geometry.dispose());
      material.dispose();
      if (scene.environment) scene.environment.dispose();
      renderer.dispose();
    }
  };
}
